"use client"

import { useReadContract, useWriteContract, useWaitForTransactionReceipt, useChainId, useAccount } from "wagmi"
import { formatEther, parseEther } from "viem"
import { useState, useEffect } from "react"
import { TKS_CLAIM_VAULT_ABI, TKS_CLAIM_VAULT_ADDRESSES, CLAIM_NETWORKS } from "./tks-claim-vault-abi"

/**
 * Get the TKS Claim Vault address for the current chain
 */
export function useTKSClaimVaultAddress(): `0x${string}` | null {
    const chainId = useChainId()
    return TKS_CLAIM_VAULT_ADDRESSES[chainId] || null
}

/**
 * Check if current chain supports TKS claiming
 */
export function useIsClaimNetwork(): boolean {
    const chainId = useChainId()
    return CLAIM_NETWORKS.includes(chainId)
}

/**
 * Fetch aggregated TKS claims from all networks via API
 * @param overrideAddress - Optional address to use instead of wagmi account (for non-EVM wallets)
 */
export function useAggregatedClaims(overrideAddress?: string | null) {
    const { address: evmAddress } = useAccount()
    // Use override address (for Solana, Tron, TON) if provided, otherwise use EVM address
    const address = overrideAddress || evmAddress
    const [data, setData] = useState<{
        totalTksEarned: string
        totalAlreadyClaimed: string
        totalAvailableToClaim: string
        totalWalletBalance: string
        // Wei versions for precision
        totalTksEarnedWei: string
        totalAlreadyClaimedWei: string
        totalAvailableToClaimWei: string
        totalWalletBalanceWei: string
        networks: { network: string; chainId: number; tksEarned: string; alreadyClaimed: string; availableToClaim: string; walletBalance: string }[]
    } | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const refetch = async () => {
        if (!address) {
            setData(null)
            return
        }

        setIsLoading(true)
        setError(null)

        try {
            const response = await fetch(`/api/claim/aggregate?address=${address}`, {
                cache: 'no-store'
            })
            if (!response.ok) {
                throw new Error("Failed to fetch claims")
            }
            const result = await response.json()
            setData(result)
        } catch (err) {
            setError(err instanceof Error ? err.message : "Unknown error")
            setData(null)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        refetch()
    }, [address])

    return { data, isLoading, error, refetch }
}

/**
 * Fetch user's claim info from the vault contract
 */
export function useTKSClaimInfo() {
    const { address } = useAccount()
    const vaultAddress = useTKSClaimVaultAddress()

    const { data, isLoading, error, refetch } = useReadContract({
        address: vaultAddress || undefined,
        abi: TKS_CLAIM_VAULT_ABI,
        functionName: "getUserInfo",
        args: address ? [address] : undefined,
        query: {
            enabled: !!vaultAddress && !!address,
            refetchInterval: 30000,
        },
    })

    const claimInfo = data ? {
        totalClaimable: Number(formatEther(data[0])),
        alreadyClaimed: Number(formatEther(data[1])),
        availableToClaim: Number(formatEther(data[2])),
        nonce: data[3] ? data[3].toString() : "0",
    } : null

    return { claimInfo, isLoading, error, refetch }
}

/**
 * Check if claiming is enabled on the vault
 */
export function useClaimingEnabled() {
    const vaultAddress = useTKSClaimVaultAddress()

    const { data, isLoading } = useReadContract({
        address: vaultAddress || undefined,
        abi: TKS_CLAIM_VAULT_ABI,
        functionName: "claimingEnabled",
        query: {
            enabled: !!vaultAddress,
        },
    })

    return { isEnabled: data ?? false, isLoading }
}

/**
 * Sync claimable amount before claiming
 */
export function useSyncBeforeClaim() {
    const { address } = useAccount()
    const chainId = useChainId()
    const [isSyncing, setIsSyncing] = useState(false)
    const [syncError, setSyncError] = useState<string | null>(null)
    const [syncResult, setSyncResult] = useState<{
        availableToClaim: string
        synced: boolean
    } | null>(null)

    const sync = async (): Promise<boolean> => {
        if (!address || !CLAIM_NETWORKS.includes(chainId)) {
            return false
        }

        setIsSyncing(true)
        setSyncError(null)

        try {
            const response = await fetch('/api/claim/sync', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ address, vaultChainId: chainId }),
            })

            const result = await response.json()

            if (!response.ok) {
                throw new Error(result.error || 'Sync failed')
            }

            setSyncResult({
                availableToClaim: result.availableToClaim,
                synced: result.success,
            })

            return true
        } catch (err) {
            setSyncError(err instanceof Error ? err.message : 'Sync failed')
            return false
        } finally {
            setIsSyncing(false)
        }
    }

    return { sync, isSyncing, syncError, syncResult }
}

/**
 * Execute TKS claim transaction with signature
 */
export function useTKSClaim() {
    const vaultAddress = useTKSClaimVaultAddress()
    const { writeContract, data: hash, isPending, error, reset } = useWriteContract()
    const { isLoading: isConfirming, isSuccess } = useWaitForTransactionReceipt({ hash })

    const claim = (amount: string, nonce: string, signature: string) => {
        if (!vaultAddress) {
            console.error("No vault address for current network")
            return
        }

        writeContract({
            address: vaultAddress,
            abi: TKS_CLAIM_VAULT_ABI,
            functionName: "claim",
            args: [BigInt(amount), BigInt(nonce), signature as `0x${string}`],
        })
    }

    const claimExact = (payout: string, nonce: string, signature: string) => {
        if (!vaultAddress) {
            console.error("No vault address for current network")
            return
        }

        writeContract({
            address: vaultAddress,
            abi: TKS_CLAIM_VAULT_ABI,
            functionName: "claimExact",
            args: [BigInt(payout), BigInt(nonce), signature as `0x${string}`],
        })
    }

    return {
        claim,
        claimExact,
        hash,
        isPending,
        isConfirming,
        isSuccess,
        error,
        reset,
    }
}

/**
 * Combined hook for claim functionality with signature fetch
 */
export function useTKSClaimVault() {
    const isClaimNetwork = useIsClaimNetwork()
    const vaultAddress = useTKSClaimVaultAddress()
    const chainId = useChainId()
    const { address } = useAccount()
    const { claimInfo, isLoading: claimInfoLoading, refetch: refetchClaimInfo } = useTKSClaimInfo()
    const { isEnabled: claimingEnabled, isLoading: claimingEnabledLoading } = useClaimingEnabled()
    const { data: aggregatedData, isLoading: aggregatedLoading, refetch: refetchAggregated } = useAggregatedClaims()
    const { claim: executeClaimLegacy, claimExact: executeClaimExact, hash, isPending, isConfirming, isSuccess, error, reset } = useTKSClaim()

    const [isSyncing, setIsSyncing] = useState(false)
    const [signatureError, setSignatureError] = useState<string | null>(null)
    const [optimisticDeltaWei, setOptimisticDeltaWei] = useState<bigint>(BigInt(0))
    const [pendingDeltaWei, setPendingDeltaWei] = useState<bigint>(BigInt(0))

    // VERIFIED claim amount from sign API (source of truth)
    const [verifiedClaimableWei, setVerifiedClaimableWei] = useState<bigint | null>(null)
    const [signedAmountWei, setSignedAmountWei] = useState<bigint | null>(null)
    const [isVerifying, setIsVerifying] = useState(false)

    const refetchAll = () => {
        refetchClaimInfo()
        refetchAggregated()
        // Also refresh verified amount
        fetchVerifiedClaimable()
    }

    // Fetch the verified claimable amount from sign API
    const fetchVerifiedClaimable = async () => {
        if (!address || !isClaimNetwork || !chainId) return

        setIsVerifying(true)
        try {
            const response = await fetch(`/api/claim/sign?address=${address}&vaultChainId=${chainId}`)
            const data = await response.json()

            if (response.ok && data.availableToClaim && (data.payout || data.amount)) {
                setVerifiedClaimableWei(BigInt(data.payout || data.availableToClaim))
                setSignedAmountWei(BigInt(data.amount))
            }
        } catch (e) {
            console.warn("Failed to fetch verified claimable amount", e)
        } finally {
            setIsVerifying(false)
        }
    }

    // Fetch verified amount on mount and when chain/address changes
    useEffect(() => {
        // Reset transaction state on network/address change
        reset()

        if (isClaimNetwork && address) {
            fetchVerifiedClaimable()
        } else {
            setVerifiedClaimableWei(null)
            setSignedAmountWei(null)
        }
    }, [isClaimNetwork, address, chainId])

    // Reset optimistic state when data actually updates
    useEffect(() => {
        if (claimInfo && claimInfo.availableToClaim === 0 && optimisticDeltaWei > BigInt(0)) {
            setOptimisticDeltaWei(BigInt(0))
        }
    }, [claimInfo?.availableToClaim])

    // Record claim success in DB
    useEffect(() => {
        const recordClaimInDb = async () => {
            if (isSuccess && hash && address) {
                // Use the delta we calculated during signing phase
                const currentPending = pendingDeltaWei
                if (currentPending <= BigInt(0)) return

                setOptimisticDeltaWei(currentPending)

                try {
                    const weiAmount = currentPending.toString()
                    console.log(`Recording claim in DB: ${formatEther(currentPending)} TKS (${weiAmount} wei)...`)
                    await fetch('/api/claim/record', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            address,
                            amount: weiAmount,
                            txHash: hash,
                            chainId,
                        })
                    })

                    // Delay refetch slightly to account for RPC/DB propagation
                    setTimeout(() => {
                        refetchAll()
                        setPendingDeltaWei(BigInt(0)) // Clear pending
                    }, 2000)

                    // Second refetch after a longer delay to be extra sure
                    setTimeout(() => {
                        refetchAll()
                        setOptimisticDeltaWei(BigInt(0)) // Final reset
                    }, 10000)
                } catch (err) {
                    console.error("Failed to record claim in DB:", err)
                }
            }
        }

        if (isSuccess) {
            recordClaimInDb()
        }
    }, [isSuccess, hash])

    // Claim with signature: fetches signature from backend then executes
    const claimWithSignature = async () => {
        if (!address || !chainId) return

        setIsSyncing(true)
        setSignatureError(null)

        try {
            const response = await fetch(`/api/claim/sign?address=${address}&vaultChainId=${chainId}`)
            const data = await response.json()

            if (!response.ok) {
                throw new Error(data.error || "Failed to fetch signature")
            }

            // Trust the API's payout calculation for 100% accuracy
            const payoutWei = BigInt(data.payout || data.availableToClaim)
            setPendingDeltaWei(payoutWei)

            // Execute the on-chain claim using the original function (selector 0x5eddd157)
            // Use 'claim' instead of 'claimExact' for better block explorer display
            executeClaimLegacy(data.payout || data.amount, data.nonce, data.signature)

        } catch (err) {
            setSignatureError(err instanceof Error ? err.message : "Signing failed")
        } finally {
            setIsSyncing(false)
        }
    }

    return {
        // Network info
        isClaimNetwork,
        vaultAddress,
        claimingEnabled,

        // Claim info from contract
        claimInfo: claimInfo ? {
            ...claimInfo,
            availableToClaim: Math.max(0, claimInfo.availableToClaim - Number(formatEther(optimisticDeltaWei)))
        } : null,

        // Aggregated data from all networks
        aggregatedData: aggregatedData ? {
            ...aggregatedData,
            totalAlreadyClaimed: formatEther(BigInt(aggregatedData.totalAlreadyClaimedWei || "0") + optimisticDeltaWei),
            totalAvailableToClaim: formatEther(BigInt(aggregatedData.totalAvailableToClaimWei || "0") - optimisticDeltaWei)
        } : null as {
            totalTksEarned: string;
            totalAlreadyClaimed: string;
            totalAvailableToClaim: string;
            totalWalletBalance: string;
            totalTksEarnedWei: string;
            totalAlreadyClaimedWei: string;
            totalAvailableToClaimWei: string;
            totalWalletBalanceWei: string;
            networks: { network: string; chainId: number; tksEarned: string; alreadyClaimed: string; availableToClaim: string; walletBalance: string }[];
        } | null,

        // Verified claimable amount (from sign API - source of truth for claims)
        // With 1.2.0, this is the EXACT payout from the API
        verifiedClaimableWei: verifiedClaimableWei !== null
            ? (verifiedClaimableWei - optimisticDeltaWei > BigInt(0) ? verifiedClaimableWei - optimisticDeltaWei : BigInt(0))
            : null,
        verifiedClaimable: verifiedClaimableWei !== null
            ? formatEther(verifiedClaimableWei - optimisticDeltaWei > BigInt(0) ? verifiedClaimableWei - optimisticDeltaWei : BigInt(0))
            : null,
        signedAmountWei,

        // Loading states
        isLoading: claimInfoLoading || claimingEnabledLoading || aggregatedLoading,
        isSyncing,
        isVerifying,

        // Claim transaction
        claim: claimWithSignature,
        hash,
        isPending: isPending || isSyncing,
        isConfirming,
        isSuccess,
        error: error || (signatureError ? new Error(signatureError) : undefined),

        // Manual sync (now basically just a revalidator)
        sync: async () => { refetchAll(); return true; },

        // Refetch
        refetchAll,
    }
}

